import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('player')

import { defineStore } from 'pinia'
import { Howl } from 'howler'
import type { Track, Content, Title, Chapter } from '@/types'
import { AudioService } from '@/services'
import type { VideoService } from '@/services/video.service'
import {
  chaptersToTracks,
  contentToTrack,
  resolveChaptersForPlayback,
  chapterIsPeekPreview,
  getActivePeekPreviewChapters,
  sortChaptersActive,
  hasFullChapterAccessForPlayback,
  getPeekContinueListeningAction,
  getChapterPlaybackUrl,
  titleNeedsFullChapterFetch,
} from '@/utils/helpers'

/** When there is no dedicated preview file, clip the first episode to this many seconds. */
const PREVIEW_CLIP_SECONDS = 20
import { useLibraryStore } from '@/stores/library'
import { ServiceContainer } from '@/services/service-container'
import { useAuthStore } from './auth'
import { StorageService } from '@/services/storage.service'
import { getConfig } from '@/config'
import { BUILD_FEATURES } from '@/config/build-features'
import { TrackingEvent } from '@/services/tracking.service'
import { SyncEventType } from '@/types/sync-v2'
import { SyncService } from '@/services/sync-v2/reliable-sync-service'
import {
  applyStickyPlayedTimestamps,
  normalizePlayedAudioProgressFields,
  resolvePlayedAudioPosition,
} from '@/utils/played-content-order'
import { ensureTitlePlaybackAllowed } from '@/composables/useAgeVerification'
import { openFinishedTitle } from '@/utils/title-completion'

// Check if Media Session API is supported
const isMediaSessionSupported = typeof navigator !== 'undefined' && 'mediaSession' in navigator

// Infer audio format(s) for Howler when URL lacks extension
const determineAudioFormats = (url: string): string[] => {
  if (!url) return ['mp3', 'aac', 'm4a', 'ogg']
  const ext = url.split('?')[0].split('#')[0].split('.').pop()?.toLowerCase()
  switch (ext) {
    case 'mp3': return ['mp3']
    case 'm4a':
    case 'aac': return ['aac', 'm4a']
    case 'ogg': return ['ogg']
    case 'wav': return ['wav']
    case 'flac': return ['flac']
    default:
      // Safe defaults covering common browser support
      return ['mp3', 'aac', 'm4a', 'ogg']
  }
}

interface PlayerState {
  currentTrack: Track | null
  queue: Track[]
  originalQueue: Track[]
  isPlaying: boolean
  currentTime: number
  duration: number
  volume: number
  repeatMode: 'off' | 'all' | 'one'
  sound: Howl | null
  progressInterval: number | null
  audioService: AudioService | null
  videoService: VideoService | null
  currentTitle: Title | null
  lastSyncTime: number
  lastSaveTime: number
  isPreviewMode: boolean
  _previewTimeout: NodeJS.Timeout | null
  _isStartingPreview: boolean
  /**
   * True while playVideo owns setSource/playWhenReady for a track switch.
   * Prevents VideoPlayer watchers/remount from racing a second HLS load (stuck buffering).
   */
  _isStartingVideoPlayback: boolean
  /**
   * Monotonic id for playVideo / beginOwnedVideoStart ownership.
   * Stale starts must not clear `_isStartingVideoPlayback` while a newer hop is in flight.
   */
  _videoStartGeneration: number
  /**
   * Monotonic id for playChapters / playFrom* entry points.
   * Async chapter fetches must not overwrite a newer title the user already started.
   */
  _playbackGeneration: number
  /** True when previewing the first episode without an explicit peek/preview file (time-capped). */
  _previewClipLimited: boolean
  _isPausing: boolean
  _lastTrackedQuartile: number
  videoPlayerMode: 'mini' | 'inline' | 'full' | 'fullscreen' | null
  /** Playing only sneak-peek chapters; last track should offer subscribe/purchase, not Finished */
  peekPlaybackSession: boolean
  /** Seek target applied once the next Howl instance has loaded (bookmark playback). */
  pendingSeekPosition: number | null
}

export const usePlayerStore = defineStore('player', {
  state: (): PlayerState => ({
    currentTrack: null,
    queue: [],
    originalQueue: [],
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    repeatMode: 'off',
    sound: null,
    progressInterval: null as number | null,
    audioService: null,
    videoService: null,
    currentTitle: null,
    lastSyncTime: 0,
    lastSaveTime: 0,
    isPreviewMode: false,
    _previewTimeout: null,
    _isStartingPreview: false,
    _isStartingVideoPlayback: false,
    _videoStartGeneration: 0,
    _playbackGeneration: 0,
    _previewClipLimited: false,
    _isPausing: false,
    _lastTrackedQuartile: 0,
    videoPlayerMode: null,
    peekPlaybackSession: false,
    pendingSeekPosition: null,
  }),

  actions: {
    /** Fetch full title with chapters when list/detail payload omitted them (e.g. home tiles). */
    async resolvePlaybackTitle(title: Title): Promise<Title> {
      if (!titleNeedsFullChapterFetch(title)) {
        return title
      }

      try {
        const { ContentService } = await import('@/services/content.service')
        const contentService = new ContentService()
        const response = await contentService.fetchContentById<Title>('titles', String(title.id), [
          'categories',
          'people',
          'contributions',
          'contributions.person',
          'chapters',
          'series',
          'channel',
          'tags',
          'rating',
          'content-type',
        ])
        const fullTitle = (Array.isArray(response.data) ? response.data[0] : response.data) as Title | undefined
        if (fullTitle?.chapters?.length) {
          return { ...title, ...fullTitle, chapters: fullTitle.chapters }
        }
      } catch (error) {
        fileLogger.error('Failed to fetch title chapters for playback:', error)
      }

      return title
    },

    /** True when the current route is the detail page for currentTitle. */
    isOnCurrentTitleDetailPage(): boolean {
      const titleId = this.currentTitle?.id
      if (titleId == null || typeof window === 'undefined') return false
      return window.location.pathname.includes(`/titles/${titleId}`)
    },

    /**
     * Mark that playVideo will own the upcoming source/play so VideoPlayer
     * does not race a second setSource (HLS → stuck buffering).
     * Call before setTrack when the next track is video.
     */
    async beginOwnedVideoStart(nextTrack?: Track | null) {
      if (nextTrack?.type !== 'video') return

      this._videoStartGeneration++
      this._isStartingVideoPlayback = true
      if (this.videoService) {
        try {
          await this.videoService.pause()
        } catch (error) {
          fileLogger.warn('Could not pause video before owned start:', error)
        }
      }
    },

    /** Bump + return id for a user-initiated title start (Play / Continue / From beginning). */
    beginPlaybackRequest(): number {
      return ++this._playbackGeneration
    },

    isCurrentPlaybackRequest(requestId: number): boolean {
      return requestId === this._playbackGeneration
    },

    getVideoUrlForCurrentTrack(): string | undefined {
      if (!this.currentTrack) return undefined
      if (this.currentTrack.url) return this.currentTrack.url

      const chapter = this.currentTitle?.chapters?.find(
        ch =>
          String(ch.id) === String(this.currentTrack!.id) ||
          (ch.url && ch.url === this.currentTrack!.url)
      )
      if (chapter) {
        const url = getChapterPlaybackUrl(chapter)
        return url || undefined
      }

      return undefined
    },

    setPeekPlaybackSession(title: Title, chaptersToPlay: Chapter[]) {
      const libraryStore = useLibraryStore()
      const authStore = useAuthStore()
      const purchased = libraryStore.purchased.some(t => String(t.id) === String(title.id))
      const subscribed = Boolean(authStore.user?.subscribed)
      this.peekPlaybackSession = Boolean(
        chaptersToPlay.length > 0 &&
          chaptersToPlay.every(ch => chapterIsPeekPreview(ch)) &&
          !hasFullChapterAccessForPlayback(title, { purchased, subscribed })
      )
    },

    ensurePlaybackQueueForCurrentTitle() {
      if (this.queue.length > 0 || !this.currentTrack) return

      if (!this.currentTitle?.chapters?.length) {
        this.setQueue([this.currentTrack])
        return
      }

      const libraryStore = useLibraryStore()
      const authStore = useAuthStore()
      const chaptersToPlay = resolveChaptersForPlayback(this.currentTitle, {
        purchased: libraryStore.purchased.some(
          t => String(t.id) === String(this.currentTitle!.id)
        ),
        subscribed: Boolean(authStore.user?.subscribed),
      })

      if (chaptersToPlay?.length) {
        this.setQueue(chaptersToTracks(this.currentTitle, chaptersToPlay))
        return
      }

      this.setQueue([this.currentTrack])
    },

    shouldAcceptTrackEnd(): boolean {
      const seekPosition =
        this.currentTrack?.type === 'video'
          ? this.currentTime
          : typeof this.sound?.seek() === 'number'
            ? (this.sound.seek() as number)
            : this.currentTime

      const reportedDuration =
        this.duration ||
        this.sound?.duration() ||
        this.currentTrack?.duration ||
        0

      // HLS often reports a non-finite duration when the media element ends.
      if (!Number.isFinite(reportedDuration) || reportedDuration <= 5) {
        return true
      }

      const slack = this.currentTrack?.type === 'video' ? 8 : 3
      const nearEnd = (position: number) =>
        Number.isFinite(position) && position >= reportedDuration - slack

      // Howl resets seek to 0 inside the end handler. The last progress tick is still currentTime.
      if (nearEnd(seekPosition) || nearEnd(this.currentTime)) {
        return true
      }

      fileLogger.warn('Ignoring premature track end event', {
        position: seekPosition,
        currentTime: this.currentTime,
        reportedDuration,
        trackId: this.currentTrack?.id,
      })
      return false
    },

    schedulePreviewTimeout(content: Title) {
      if (!this._previewClipLimited) return

      if (this._previewTimeout) {
        clearTimeout(this._previewTimeout)
      }
      // Safety net if currentTime never advances (e.g. stalled progress updates).
      // Primary limit is checkPreviewClipLimit via playback position.
      // Do not require isPlaying — video HLS often still buffering when a short timer would fire.
      this._previewTimeout = setTimeout(() => {
        if (
          this.isPreviewMode &&
          this._previewClipLimited &&
          String(this.currentTitle?.id) === String(content.id)
        ) {
          fileLogger.log('Preview safety timeout reached, stopping')
          this.stopPreview()
        }
      }, PREVIEW_CLIP_SECONDS * 3 * 1000)
    },

    /** Stop time-capped previews once playback position reaches the clip limit. */
    checkPreviewClipLimit() {
      if (!this.isPreviewMode || !this._previewClipLimited) return
      if (this.currentTime < PREVIEW_CLIP_SECONDS) return

      fileLogger.log(
        `Preview clip limit reached (${PREVIEW_CLIP_SECONDS}s of content), stopping`
      )
      this.stopPreview()
    },

    async play() {
      if (!this.currentTrack) {
        fileLogger.log('No current track, cannot play')
        return
      }

      try {
        fileLogger.log('Play called for track:', this.currentTrack.title)

        // Video.js path only when the app actually supports video (Heard does not)
        if (this.currentTrack.type === 'video' && getConfig().features.video) {
          return this.playVideo()
        }

        // Check if we need to create a new sound object
        if (!this.sound || (this.sound as any)._src !== this.currentTrack.url) {
          fileLogger.log('Creating new sound object')
          
          // Cleanup old sound if exists
          if (this.sound) {
            try {
              this.sound.unload()
            } catch (error) {
              fileLogger.error('Error unloading previous sound:', error)
            }
          }

          // Validate URL - make sure it's a non-empty string
          const audioUrl = this.currentTrack.url;
          if (!audioUrl || typeof audioUrl !== 'string') {
            fileLogger.error('Invalid URL for audio:', audioUrl);
            throw new Error('Invalid audio URL');
          }

          try {
            // Create new sound object with all the necessary event handlers
            this.sound = new Howl({
              src: [audioUrl],
              html5: true,
              format: determineAudioFormats(audioUrl),
              volume: this.volume,
              onend: () => {
                fileLogger.log('Track ended')
                this.isPlaying = false
                this.handleTrackEnd()
              },
              onplay: () => {
                fileLogger.log('Track started playing')
                this.isPlaying = true
                // Apply bookmark seek once playback starts (html5 streams may ignore seek before play)
                if (this.pendingSeekPosition !== null && this.pendingSeekPosition > 0) {
                  const seekTo = this.pendingSeekPosition
                  this.pendingSeekPosition = null
                  this.sound?.seek(seekTo)
                  this.currentTime = seekTo
                  if (this.currentTitle && this.currentTrack && !this.isPreviewMode) {
                    this.updatePlaybackPosition(seekTo)
                  }
                }
                // Update media session when playback starts, but only after the sound is fully initialized
                setTimeout(() => {
                  this.updateMediaSession()
                }, 50)
              },
              onpause: () => {
                fileLogger.log('Track paused')
                this.isPlaying = false
              },
              onload: () => {
                fileLogger.log('Track loaded, duration:', this.sound?.duration())
                this.duration = this.sound?.duration() ?? 0
              },
              onstop: () => {
                fileLogger.log('Track stopped')
                this.isPlaying = false
              },
              onloaderror: (id, error) => {
                fileLogger.error('Error loading track:', error)
                this.isPlaying = false
                // Handle load error - maybe show a notification to the user
              },
              onplayerror: (_id, error) => {
                fileLogger.error('Error playing track:', error)
                // Play was called after load in a previous version, which expired the
                // user-gesture window. Retry once the AudioContext unlocks.
                this.sound?.once('unlock', () => {
                  this.sound?.play()
                })
              }
            })
          } catch (error: any) {
            fileLogger.error('Error initializing sound:', error)
            throw new Error(`Could not initialize audio: ${error.message}`)
          }
        }

        // Sound is initialized, try to play it
        try {
          if (this.sound) {
            // Seek after load (before play) when resuming from a bookmark position
            if (this.pendingSeekPosition !== null && this.pendingSeekPosition > 0) {
              const seekTo = this.pendingSeekPosition
              this.sound.seek(seekTo)
              this.currentTime = seekTo
            }

            this.sound.play()
            this.isPlaying = true
            this.startProgressUpdates()
            this.updateMediaSession()
            
            // Track play event
            try {
              if (this.currentTitle && this.currentTrack) {
                const services = ServiceContainer.getInstance()
                const trackingService = await services.get('tracking')
                const trackId = typeof this.currentTrack.id === 'number' 
                  ? this.currentTrack.id.toString() 
                  : (this.currentTrack.id || '')
                
                await trackingService.trackPlaybackAction(
                  this.currentTitle.id.toString(),
                  trackId,
                  this.currentTrack.title || this.currentTitle.title,
                  TrackingEvent.PlaybackPlay,
                  this.currentTime,
                  this.duration,
                  false
                )
              }
            } catch (error) {
              fileLogger.error('Error tracking play event:', error)
            }
          } else {
            throw new Error('Sound object is null after initialization')
          }
        } catch (playError: any) {
          fileLogger.error('Error starting playback:', playError)
          this.isPlaying = false
          throw new Error(`Could not start playback: ${playError.message}`)
        }
      } catch (error) {
        fileLogger.error('Playback failed:', error)
        this.isPlaying = false
        // Maybe show an error notification to the user
      }
    },

    async pause() {
      fileLogger.log('pause() called')
      
      // Check if we're already in the process of pausing to prevent race conditions
      if (this._isPausing) {
        fileLogger.log('Already pausing, ignoring duplicate call')
        return
      }
      
      // Set the pausing flag to prevent duplicate calls
      this._isPausing = true
      
      try {
        // Handle video content
        if (this.currentTrack?.type === 'video') {
          this.pauseVideo()
          return
        }

        if (this.sound) {
          // Get current position before pausing
          const currentPosition = this.sound.seek() as number
          fileLogger.log('Current position before pause:', currentPosition)

          // Keep store clock in sync before save/sync (syncPlaybackPosition reads currentTime)
          if (typeof currentPosition === 'number' && Number.isFinite(currentPosition)) {
            this.currentTime = currentPosition
          }
          
          // Pause the sound
          this.sound.pause()
          
          // Explicitly set isPlaying to false immediately
          this.isPlaying = false
          
          // Stop progress updates
          this.stopProgressUpdates()
          
          // Save position to library store only if NOT in preview mode
          if (this.currentTitle && this.currentTrack && !this.isPreviewMode) {
            fileLogger.log('Saving position on pause:', this.currentTime)
            this.updatePlaybackPosition(this.currentTime)

            // Force an immediate storage save (bypassing the progress-tick throttle)
            this.lastSaveTime = Date.now()
            // Await so a quick reload/navigation cannot race past an in-flight write
            await this.savePlaybackState()
            await this.syncPlaybackPosition()
            this.lastSyncTime = Date.now()
          }
          
          // Track pause event
          try {
            if (this.currentTitle && this.currentTrack) {
              const services = ServiceContainer.getInstance()
              const trackingService = await services.get('tracking')
              const trackId = typeof this.currentTrack.id === 'number' 
                ? this.currentTrack.id.toString() 
                : (this.currentTrack.id || '')
                
              await trackingService.trackPlaybackAction(
                this.currentTitle.id.toString(),
                trackId,
                this.currentTrack.title || this.currentTitle.title,
                TrackingEvent.PlaybackPause,
                currentPosition,
                this.duration,
                false
              )
            }
          } catch (error) {
            fileLogger.error('Error tracking pause event:', error)
          }
        }
      } catch (error) {
        fileLogger.error('Error during pause:', error)
      } finally {
        // Reset the pausing flag after a short delay to prevent race conditions
        setTimeout(() => {
          this._isPausing = false
          fileLogger.log('Pause operation completed, reset flag')
        }, 300)
      }
    },

    setTrack(track: Track) {
      fileLogger.log('Setting track:', track?.title)
      
      // Skip if trying to set the same track again
      if (this.currentTrack && track && String(this.currentTrack.id) === String(track.id) && this.currentTrack.url === track.url) {
        fileLogger.log('Track is the same as current track, skipping')
        return
      }
      
      // Clean up existing sound if any
      if (this.sound) {
        fileLogger.log('Cleaning up existing sound')
        try {
          this.sound.stop()
          this.sound.unload()
          this.sound = null
        } catch (error) {
          fileLogger.error('Error cleaning up sound:', error)
        }
      }
      
      // Stop any progress updates
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
      
      // Update track
      this.currentTrack = track
      this.isPlaying = false
      this.currentTime = 0
      this.duration = 0
      
      if (track) {
        fileLogger.log(`Track set to: ${track.title} (ID: ${track.id})`)
      } else {
        fileLogger.log('Track cleared (set to null)')
      }
      
      // Only update media session metadata when track changes once we've set all track properties
      // Use setTimeout to ensure this runs after the current execution cycle
      setTimeout(() => {
        this.updateMediaSession()
      }, 0)
    },

    setQueue(tracks: Track[]) {
      this.queue = tracks
    },

    async playFromBeginning(content: Title) {
      fileLogger.log('playFromBeginning called with content:', content)

      const requestId = this.beginPlaybackRequest()

      if (!(await ensureTitlePlaybackAllowed(content))) return
      if (!this.isCurrentPlaybackRequest(requestId)) return

      const resolvedContent = await this.resolvePlaybackTitle(content)
      if (!this.isCurrentPlaybackRequest(requestId)) return
      
      // Reset preview mode when playing full content
      this.isPreviewMode = false
      fileLogger.log('Normal playback mode - will store in library')
      
      // Add to library played content
      const libraryStore = useLibraryStore()
      libraryStore.addToPlayed(resolvedContent)
      
      // Check if content has chapters
      if (resolvedContent.chapters?.length) {
        fileLogger.log('Content has chapters, setting up chapter queue')

        const authStore = useAuthStore()
        const chaptersToPlay = resolveChaptersForPlayback(resolvedContent, {
          purchased: libraryStore.purchased.some(t => String(t.id) === String(resolvedContent.id)),
          subscribed: Boolean(authStore.user?.subscribed),
        })
        if (!chaptersToPlay?.length) {
          fileLogger.log('No chapters playable for current access level')
          return
        }

        this.setPeekPlaybackSession(resolvedContent, chaptersToPlay)

        // Convert chapters to tracks
        const tracks = chaptersToTracks(resolvedContent, chaptersToPlay)
        
        // Reset the queue with new content's chapters
        this.setQueue(tracks)
        this.currentTitle = resolvedContent
        
        await this.beginOwnedVideoStart(tracks[0])
        if (!this.isCurrentPlaybackRequest(requestId)) return
        // Set the first track
        this.setTrack(tracks[0])
      } else {
        // No chapters, use the single track approach
        fileLogger.log('Content has no chapters, using single track approach')
        const singleTrack = contentToTrack(content)
        await this.beginOwnedVideoStart(singleTrack)
        if (!this.isCurrentPlaybackRequest(requestId)) return
        this.setTrack(singleTrack)
        
        // Clear the previous queue and set a single-track queue
        this.setQueue([this.currentTrack!])
        this.currentTitle = content
      }
      
      // Update playback position to 0
      this.updatePlaybackPosition(0)
      
      // Play the track
      await this.play()
    },

    async playFromLastPosition(content: Title) {
      fileLogger.log('playFromLastPosition called with content:', content)

      const requestId = this.beginPlaybackRequest()

      if (!(await ensureTitlePlaybackAllowed(content))) return
      if (!this.isCurrentPlaybackRequest(requestId)) return

      // Always resolve chapters first — played/home tiles often omit URLs, and returning
      // early here left the second title stuck with no episodes loaded.
      const resolvedContent = await this.resolvePlaybackTitle(content)
      if (!this.isCurrentPlaybackRequest(requestId)) return
      
      // Find the title in played_content to get the last position - ensure string comparison
      const libraryStore = useLibraryStore()
      const contentIdString = String(resolvedContent.id)
      const playedTitle = libraryStore.played_content.find(item => String(item.id) === contentIdString)
      
      fileLogger.log('Found played title:', playedTitle)
      
      if (!playedTitle) {
        // If not found in played_content, just play from beginning
        fileLogger.log('No played title found, playing from beginning')
        return this.playChapters(resolvedContent, undefined, false, undefined, { requestId })
      }
      
      // Store the position to seek to after playback starts (web queuePosition or API position)
      const seekPosition = resolvePlayedAudioPosition(playedTitle)
      
      fileLogger.log('Seek position:', seekPosition)

      const authStore = useAuthStore()
      const allowedChapters = resolveChaptersForPlayback(resolvedContent, {
        purchased: libraryStore.purchased.some(t => String(t.id) === String(resolvedContent.id)),
        subscribed: Boolean(authStore.user?.subscribed),
      })
      if (!allowedChapters?.length) {
        fileLogger.log('No playable chapters for current access level')
        return Promise.resolve()
      }
      
      // CRITICAL: Handle both chapter_id (from mobile sync) and queueIndex (from web or mobile)
      // chapter_id is the actual chapter database ID, queueIndex can be either:
      // - chapter_id (when stored by web app)
      // - playlist index (when synced from mobile app)
      let targetChapter: Chapter | undefined = undefined
      
      if (resolvedContent.chapters?.length) {
        // First, try to resolve by chapter_id if present (from mobile sync)
        const chapterId = (playedTitle as any).chapter_id
        if (chapterId !== undefined && chapterId !== null) {
          const chapterIdString = String(chapterId)
          fileLogger.log('Found chapter_id from sync, searching for chapter:', chapterIdString)
          
          targetChapter = resolvedContent.chapters.find(chapter => 
            String(chapter.id) === chapterIdString
          )
          
          if (targetChapter) {
            fileLogger.log('Found chapter by chapter_id:', targetChapter)
          } else {
            fileLogger.warn('Could not find chapter with chapter_id:', chapterIdString)
          }
        }
        
        // If chapter_id lookup failed, try queueIndex
        if (!targetChapter && playedTitle.queueIndex !== undefined && playedTitle.queueIndex !== null) {
          const queueIndexValue = playedTitle.queueIndex
          fileLogger.log('Trying queueIndex:', queueIndexValue)
          
          // First, try to find chapter by ID (for web-native data where queueIndex contains chapter_id)
          const queueIndexString = String(queueIndexValue)
          targetChapter = resolvedContent.chapters.find(chapter => 
            String(chapter.id) === queueIndexString
          )
          
          if (targetChapter) {
            fileLogger.log('Found chapter by queueIndex (treated as chapter_id):', targetChapter)
          } else {
            // If not found by ID, try using queueIndex as array index (for mobile-synced data)
            const queueIndexNum = typeof queueIndexValue === 'number' 
              ? queueIndexValue 
              : parseInt(String(queueIndexValue), 10)
            
            if (!isNaN(queueIndexNum) && queueIndexNum >= 0 && queueIndexNum < resolvedContent.chapters.length) {
              targetChapter = resolvedContent.chapters[queueIndexNum]
              fileLogger.log('Found chapter by queueIndex (treated as array index):', targetChapter)
            } else {
              fileLogger.warn('queueIndex is not a valid array index:', queueIndexNum)
            }
          }
        }
      }

      if (
        targetChapter &&
        !allowedChapters.some(ch => String(ch.id) === String(targetChapter!.id))
      ) {
        fileLogger.log('Saved chapter not in allowed set for access level; using first allowed')
        targetChapter = allowedChapters[0]
      }
      
      if (targetChapter) {
        fileLogger.log('Playing specific chapter:', targetChapter)
        // Play the specific chapter and skip position update to preserve the saved position
        return this.playChapters(
          resolvedContent,
          targetChapter,
          true,
          seekPosition !== null && seekPosition > 0 ? seekPosition : undefined,
          { requestId }
        )
      } else {
        fileLogger.log('No specific chapter found, playing from beginning')
        // Fallback to playing from the beginning if chapter not found
        return this.playChapters(
          resolvedContent,
          undefined,
          true,
          seekPosition !== null && seekPosition > 0 ? seekPosition : undefined,
          { requestId }
        )
      }
    },

    next() {
      if (this.isPreviewMode) {
        fileLogger.log('Next blocked in preview mode')
        return
      }
      if (!this.currentTrack || this.queue.length === 0) {
        fileLogger.log('Cannot go to next track: No current track or queue is empty')
        return
      }
      
      fileLogger.log('Attempting to go to next track')
      const currentIndex = this.queue.findIndex(track => String(track.id) === String(this.currentTrack?.id))
      fileLogger.log(`Current track index: ${currentIndex}, Queue length: ${this.queue.length}`)
      
      if (currentIndex === this.queue.length - 1) {
        // We're at the last track
        fileLogger.log('We are at the last track in the queue')
        
        if (this.repeatMode === 'all') {
          // If repeat mode is 'all', loop back to the first track
          fileLogger.log('Repeat mode is "all", looping back to first track')
          void this.beginOwnedVideoStart(this.queue[0]).then(() => {
            this.setTrack(this.queue[0])
            this.play()
          })
        } else {
          // Otherwise, stop playback
          fileLogger.log('Not in repeat mode, stopping at the end of queue')
          this.maybeTriggerPeekEndOrFinishedPage()
        }
      } else {
        // Go to the next track
        fileLogger.log(`Moving to next track at index ${currentIndex + 1}`)
        const nextTrack = this.queue[currentIndex + 1]
        void this.beginOwnedVideoStart(nextTrack).then(() => {
          this.setTrack(nextTrack)
          this.play()
        })
      }
    },

    previous() {
      if (this.isPreviewMode) {
        fileLogger.log('Previous blocked in preview mode')
        return
      }
      if (!this.currentTrack || this.queue.length === 0) {
        fileLogger.log('Cannot go to previous track: No current track or queue is empty')
        return
      }
      
      fileLogger.log('Attempting to go to previous track')
      const currentIndex = this.queue.findIndex(track => String(track.id) === String(this.currentTrack?.id))
      fileLogger.log(`Current track index: ${currentIndex}, Queue length: ${this.queue.length}`)
      
      if (currentIndex > 0) {
        // Go to the previous track
        fileLogger.log(`Moving to previous track at index ${currentIndex - 1}`)
        const prevTrack = this.queue[currentIndex - 1]
        void this.beginOwnedVideoStart(prevTrack).then(() => {
          this.setTrack(prevTrack)
          this.play()
        })
      } else {
        // We're at the first track
        fileLogger.log('We are at the first track in the queue')
        
        if (this.repeatMode === 'all') {
          // If repeat mode is 'all', loop back to the last track
          fileLogger.log('Repeat mode is "all", looping to last track')
          const lastTrack = this.queue[this.queue.length - 1]
          void this.beginOwnedVideoStart(lastTrack).then(() => {
            this.setTrack(lastTrack)
            this.play()
          })
        } else {
          // Otherwise, restart current track
          fileLogger.log('Restarting current track (first in queue)')
          this.seek(0)
        }
      }
    },

    setVolume(volume: number) {
      this.volume = volume
      
      // Handle video content
      if (this.currentTrack?.type === 'video') {
        this.setVideoVolume(volume)
        return
      }
      
      this.sound?.volume(volume)
    },

    seek(time: number) {
      fileLogger.log('Seeking to time:', time)

      // Preview mode: seekbar/skip are blocked (only internal resets to 0 allowed)
      if (this.isPreviewMode && time > 0) {
        fileLogger.log('Seek blocked in preview mode')
        return
      }
      
      // Handle video content
      if (this.currentTrack?.type === 'video') {
        this.seekVideo(time)
        return
      }
      
      if (!this.sound) return

      this.sound.seek(time)
      this.currentTime = time
      
      // Also update the library store with the new position if NOT in preview mode
      if (this.currentTitle && this.currentTrack && !this.isPreviewMode) {
        fileLogger.log('Updating playback position after seek')
        this.updatePlaybackPosition(time)
      }
      
      // Update position state when seeking
      this.updatePositionState()
    },

    setDuration(duration: number) {
      this.duration = duration
    },

    shuffleQueue() {
      this.originalQueue = [...this.queue]
      for (let i = this.queue.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this.queue[i], this.queue[j]] = [this.queue[j], this.queue[i]]
      }
    },

    restoreQueue() {
      if (this.originalQueue.length) {
        this.queue = [...this.originalQueue]
        this.originalQueue = []
      }
    },

    setRepeatMode(mode: 'off' | 'all' | 'one') {
      this.repeatMode = mode
    },

    initialize() {
      this.audioService = new AudioService()
      
      this.audioService.on('timeupdate', () => {
        this.updateProgress()
      })

      this.audioService.on('loadedmetadata', () => {
        this.setDuration(this.audioService!.getDuration())
      })

      this.audioService.on('ended', () => {
        if (this.repeatMode === 'one') {
          this.seek(0)
          this.play()
        } else {
          this.next()
        }
      })

      // Initialize Media Session API if supported
      this.initializeMediaSession()
    },

    // Method for unauthenticated users - only initialize essential functionality
    initializeBasic() {
      fileLogger.log('Initializing player with basic functionality for unauthenticated user')
      
      this.audioService = new AudioService()
      
      this.audioService.on('timeupdate', () => {
        this.updateProgress()
      })

      this.audioService.on('loadedmetadata', () => {
        this.setDuration(this.audioService!.getDuration())
      })

      this.audioService.on('ended', () => {
        if (this.repeatMode === 'one') {
          this.seek(0)
          this.play()
        } else {
          this.next()
        }
      })
      
      // Initialize Media Session API for basic playback
      this.initializeMediaSession()
      
      // Skip loading previous state or syncing with backend
    },

    startProgressUpdates() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
      }

      this.progressInterval = window.setInterval(() => {
        if (this.sound && this.isPlaying) {
          this.currentTime = this.sound.seek() as number
          
          // Update position in library store if we have a current title and NOT in preview mode
          if (this.currentTitle && !this.isPreviewMode) {
            this.updatePlaybackPosition(this.currentTime)
          }

          this.checkPreviewClipLimit()
          
          // Update media session position state periodically
          this.updatePositionState()
        }
      }, 1000)
    },

    stopProgressUpdates() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
    },

    async updateProgress() {
      if (!this.sound || !this.isPlaying) return
      
      const currentSecond = this.sound.seek() as number
      if (!currentSecond) return
      
      this.currentTime = currentSecond
      
      // Track playback milestones if we have a current title
      if (this.currentTitle && this.currentTrack && !this.isPreviewMode) {
        // We need these for milestone tracking
        const duration = this.duration
        const position = this.currentTime
        
        // Only process if we have valid duration
        if (duration > 0) {
          try {
            const quarterDuration = duration * 0.25
            const halfDuration = duration * 0.5
            const threeQuarterDuration = duration * 0.75
            
            const services = ServiceContainer.getInstance()
            const trackingService = await services.get('tracking')
            const trackId = typeof this.currentTrack.id === 'number' 
              ? this.currentTrack.id.toString() 
              : (this.currentTrack.id || '')
            
            // Track first quartile
            if (position >= quarterDuration && position <= quarterDuration + 1 && this._lastTrackedQuartile !== 1) {
              this._lastTrackedQuartile = 1
              await trackingService.trackPlaybackFirstQuartile(
                this.currentTitle.id.toString(),
                trackId,
                this.currentTrack.title || this.currentTitle.title,
                duration,
                false
              )
            }
            
            // Track midpoint
            if (position >= halfDuration && position <= halfDuration + 1 && this._lastTrackedQuartile !== 2) {
              this._lastTrackedQuartile = 2
              await trackingService.trackPlaybackMidpoint(
                this.currentTitle.id.toString(),
                trackId,
                this.currentTrack.title || this.currentTitle.title,
                duration,
                false
              )
            }
            
            // Track third quartile
            if (position >= threeQuarterDuration && position <= threeQuarterDuration + 1 && this._lastTrackedQuartile !== 3) {
              this._lastTrackedQuartile = 3
              await trackingService.trackPlaybackThirdQuartile(
                this.currentTitle.id.toString(),
                trackId,
                this.currentTrack.title || this.currentTitle.title,
                duration,
                false
              )
            }
            
            // Track every 15 seconds
            if (Math.floor(position) % 15 === 0 && position > 0 && position < duration) {
              await trackingService.trackPlaybackIntervalProgress(
                this.currentTitle.id.toString(),
                trackId,
                this.currentTrack.title || this.currentTitle.title,
                position,
                duration,
                false
              )
            }
          } catch (error) {
            // Silent error
          }
        }
      }
      
      // Update playback position in library store if we have a current title and NOT in preview mode
      if (this.currentTitle && !this.isPreviewMode) {
        this.updatePlaybackPosition(this.currentTime)
      }
      
      // Update Media Session API position state if supported
      if ('setPositionState' in navigator.mediaSession) {
        this.updatePositionState()
      }
    },

    // New method to update playback position in library store
    updatePlaybackPosition(position: number) {
      if (!this.currentTitle || !this.currentTrack) return
      
      // Skip if in preview mode
      if (this.isPreviewMode) return
      
      // Skip for video content - VideoPlayer handles this
      if (this.currentTrack.type === 'video') {
        fileLogger.log('Skipping position update for video content - VideoPlayer handles this')
        return
      }
      
      // Check authentication status first
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) {
        fileLogger.log('User not authenticated, skipping position update')
        return
      }
      
      const libraryStore = useLibraryStore()
      const titleId = this.currentTitle.id
      const chapterId = this.currentTrack.id
      
      // Convert IDs to strings for consistent comparison
      const titleIdString = String(titleId)
      const chapterIdString = String(chapterId)
      
      // Find the title in played_content or add it if not present - ensure string comparison
      const titleIndex = libraryStore.played_content.findIndex(t => String(t.id) === titleIdString)
      
      const chapterNum = parseInt(chapterIdString, 10)
      const progressFields = normalizePlayedAudioProgressFields({
        queuePosition: position,
        position,
        queueIndex: chapterIdString,
        chapter_id: Number.isNaN(chapterNum) ? chapterId : chapterNum,
      })

      if (titleIndex === -1) {
        // Add to played_content if not present
        fileLogger.log('Adding new entry to played_content:', {
          id: titleId,
          queuePosition: progressFields.queuePosition,
          queueIndex: progressFields.queueIndex,
        })
        
        // Make sure there are no duplicates (there shouldn't be, but just in case) - ensure string comparison
        libraryStore.played_content = libraryStore.played_content.filter(t => String(t.id) !== titleIdString)
        
        libraryStore.played_content.push(
          applyStickyPlayedTimestamps(null, {
            ...this.currentTitle,
            ...progressFields,
          }, { newPosition: position })
        )
      } else {
        // Update existing entry — preserve sticky dateAdded (match mobile Continue Listening)
        fileLogger.log('Updating existing entry in played_content:', {
          id: titleId,
          queuePosition: progressFields.queuePosition,
          queueIndex: progressFields.queueIndex,
        })

        const existing = libraryStore.played_content[titleIndex]
        const updated = applyStickyPlayedTimestamps(existing, {
          ...existing,
          ...progressFields,
        }, { newPosition: position })
        libraryStore.played_content[titleIndex] = updated
      }
      
      // Persist to local storage, but throttle to avoid serializing the entire
      // played_content array to storage on every 1s progress tick. The in-memory
      // store above is always up to date; storage just needs to be eventually
      // consistent (and pause/seek force an immediate save elsewhere).
      const now = Date.now()
      if (now - this.lastSaveTime > 5000) {
        this.lastSaveTime = now
        this.savePlaybackState()
      }

      // Sync with API periodically (every 10 seconds)
      if (now - this.lastSyncTime > 10000) {
        this.syncPlaybackPosition()
        this.lastSyncTime = now
      }
    },
    
    // New method to save playback state to storage and sync with backend
    async savePlaybackState() {
      if (!this.currentTitle || !this.currentTrack) return
      
      // Don't save if in preview mode
      if (this.isPreviewMode) {
        fileLogger.log('In preview mode, skipping save to storage')
        return
      }
      
      // Check authentication status first
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) {
        fileLogger.log('User not authenticated, skipping save to storage')
        return
      }
      
      try {
        const libraryStore = useLibraryStore()
        const services = ServiceContainer.getInstance()
        const storage = await services.get('storage')
        
        // Save to local storage only
        await storage.set('played_content', libraryStore.played_content)
        
        // We don't sync with backend during playback
        // A full library sync will be triggered elsewhere when needed
      } catch (error) {
        fileLogger.error('Failed to save playback state:', error)
      }
    },
    
    // New method to sync playback position with API
    resolveChapterSyncFields(): { queueIndex: number; chapterId: number | string } {
      const chapters = this.currentTitle?.chapters ?? []
      const trackId = this.currentTrack?.id
      if (!trackId) {
        return { queueIndex: 0, chapterId: 0 }
      }

      const trackIdStr = String(trackId)
      const chapterIndex = chapters.findIndex(ch => String(ch.id) === trackIdStr)

      if (chapterIndex >= 0) {
        const chapter = chapters[chapterIndex]
        const chapterId = typeof chapter.id === 'number'
          ? chapter.id
          : parseInt(String(chapter.id), 10)
        return {
          queueIndex: chapterIndex,
          chapterId: Number.isNaN(chapterId) ? trackId : chapterId,
        }
      }

      const parsed = parseInt(trackIdStr, 10)
      return {
        queueIndex: Number.isNaN(parsed) ? 0 : parsed,
        chapterId: Number.isNaN(parsed) ? trackId : parsed,
      }
    },

    async syncPlaybackPosition() {
      if (!this.currentTitle || !this.currentTrack) return
      
      // Don't sync if in preview mode
      if (this.isPreviewMode) {
        fileLogger.log('In preview mode, skipping API sync')
        return
      }
      
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) {
        fileLogger.log('User not authenticated, skipping API sync')
        return
      }
      
      try {
        fileLogger.log('Syncing playback position with API')
        const services = ServiceContainer.getInstance()
        const syncService = await services.get<SyncService>('sync')
        const position = Math.floor(this.currentTime)
        const { queueIndex, chapterId } = this.resolveChapterSyncFields()

        await syncService.queueAction(
          SyncEventType.playProgress,
          String(this.currentTitle.id),
          {
            title_id: this.currentTitle.id,
            queueIndex,
            chapter_id: chapterId,
            queuePosition: position,
            position,
            duration: this.currentTrack.duration ?? 0,
            last_updated_at: new Date().toISOString(),
          }
        )
        
        fileLogger.log(
          `[SYNC_DIAG] QUEUE played_content from player titleId=${this.currentTitle.id} chapter_id=${chapterId} queueIndex=${queueIndex} position=${position} duration=${this.currentTrack.duration ?? 0}`
        )
        if (chapterId == null) {
          fileLogger.warn(
            `[SYNC_DIAG] QUEUE played_content missing chapter_id titleId=${this.currentTitle.id} queueIndex=${queueIndex}`
          )
        }
      } catch (error) {
        fileLogger.error('Failed to sync playback position with API:', error)
      }
    },

    handleTrackEnd() {
      fileLogger.log('Track ended, handling end of track')

      if (this.isPreviewMode) {
        fileLogger.log('Preview ended, stopping preview')
        this.stopPreview()
        return
      }

      if (!this.shouldAcceptTrackEnd()) {
        return
      }
      
      if (this.repeatMode === 'one') {
        fileLogger.log('Repeat mode is "one", restarting current track')
        this.seek(0)
        this.play()
        return
      }
      
      // If we don't have a current track or the queue is empty, just return
      if (!this.currentTrack || this.queue.length === 0) {
        fileLogger.log('Cannot go to next track: No current track or queue is empty')
        return
      }
      
      const currentIndex = this.queue.findIndex(track => String(track.id) === String(this.currentTrack?.id))
      fileLogger.log(`Current track index: ${currentIndex}, Queue length: ${this.queue.length}`)
      
      // Check if we're at the end of the queue
      if (currentIndex === this.queue.length - 1) {
        // Last track
        fileLogger.log('Reached end of queue')
        
        if (this.repeatMode === 'all') {
          // Loop back to first track if repeat all is enabled
          fileLogger.log('Repeat mode is "all", looping back to first track')
          void this.beginOwnedVideoStart(this.queue[0]).then(() => {
            this.setTrack(this.queue[0])
            this.play()
          })
        } else {
          // Stop playback at the end
          fileLogger.log('End of playback, not repeating')
          this.maybeTriggerPeekEndOrFinishedPage()
        }
      } else {
        // Go to next track
        fileLogger.log(`Moving to next track at index ${currentIndex + 1}`)
        const nextTrack = this.queue[currentIndex + 1]
        void this.beginOwnedVideoStart(nextTrack).then(() => {
          this.setTrack(nextTrack)
          this.play()
        })
      }
    },

    maybeTriggerPeekEndOrFinishedPage() {
      if (!this.currentTitle) {
        fileLogger.log('No current title, skipping end-of-queue navigation')
        return
      }

      if (this.peekPlaybackSession) {
        const libraryStore = useLibraryStore()
        const authStore = useAuthStore()
        const purchased = libraryStore.purchased.some(
          t => String(t.id) === String(this.currentTitle!.id)
        )
        const subscribed = Boolean(authStore.user?.subscribed)
        const action = getPeekContinueListeningAction(this.currentTitle!, { purchased, subscribed })
        if (action) {
          fileLogger.log('Peek playback ended, navigating to continue-listening:', action)
          this.triggerContinueListeningPage(action)
          return
        }
      }

      this.triggerFinishedPage()
    },

    triggerContinueListeningPage(action: 'subscribe' | 'purchase') {
      fileLogger.log('Navigating to continue-listening page:', action)
      if (!this.currentTitle) return

      import('@/router').then(({ router }) => {
        router.push({
          name: 'continue-listening',
          params: { titleId: String(this.currentTitle!.id) },
          query: { action },
        })
        this.peekPlaybackSession = false
      }).catch(error => {
        fileLogger.error('Failed to navigate to continue-listening page:', error)
      })
    },

    triggerFinishedPage() {
      fileLogger.log('Triggering finished page navigation')
      this.peekPlaybackSession = false

      if (!this.currentTitle) {
        fileLogger.log('No current title, skipping finished page')
        return
      }

      const title = this.currentTitle
      void openFinishedTitle(title).catch(error => {
        fileLogger.error('Failed to navigate to finished page:', error)
      })
    },

    addToQueue(content: Content) {
      // Convert Content to Track if needed
      const track = {
        id: content.id,
        title: content.title,
        artist: content.artist,
        artwork: content.artwork,
        url: content.url
      }
      this.queue.push(track)
    },

    async playChapters(
      title: Title,
      startFromChapter?: Chapter,
      skipPositionUpdate: boolean = false,
      startPosition?: number,
      options: { requestId?: number } = {}
    ) {
      fileLogger.log('playChapters called with title:', title, 'startFromChapter:', startFromChapter, 'skipPositionUpdate:', skipPositionUpdate)

      const requestId = options.requestId ?? this.beginPlaybackRequest()

      if (!(await ensureTitlePlaybackAllowed(title))) return
      if (!this.isCurrentPlaybackRequest(requestId)) return

      const resolvedTitle = await this.resolvePlaybackTitle(title)
      if (!this.isCurrentPlaybackRequest(requestId)) return
      
      if (!resolvedTitle.chapters?.length) {
        fileLogger.log('No chapters found, returning')
        return
      }

      const libraryStore = useLibraryStore()
      const authStore = useAuthStore()
      const chaptersToPlay = resolveChaptersForPlayback(resolvedTitle, {
        purchased: libraryStore.purchased.some(t => String(t.id) === String(resolvedTitle.id)),
        subscribed: Boolean(authStore.user?.subscribed),
      })

      if (!chaptersToPlay?.length) {
        fileLogger.log('No chapters playable for current access level')
        return
      }

      this.setPeekPlaybackSession(resolvedTitle, chaptersToPlay)

      let startChapter = startFromChapter
      if (
        startChapter &&
        !chaptersToPlay.some(ch => String(ch.id) === String(startChapter!.id))
      ) {
        fileLogger.warn('Start chapter not in allowed set; using first playable chapter')
        startChapter = undefined
      }
      
      // Reset preview mode when playing full content
      this.isPreviewMode = false
      fileLogger.log('Normal playback mode - will store in library')
      
      // Add to library played content
      libraryStore.addToPlayed(resolvedTitle)
      
      // Convert chapters to tracks
      const tracks = chaptersToTracks(resolvedTitle, chaptersToPlay)
      
      // Set the queue
      this.setQueue(tracks)
      this.currentTitle = resolvedTitle
      
      // If starting from specific chapter, find its index - ensure string comparison
      let nextTrack = tracks[0]
      if (startChapter) {
        fileLogger.log('Starting from specific chapter:', startChapter)
        const startChapterIdString = String(startChapter.id)
        const chapterIndex = tracks.findIndex(t => String(t.id) === startChapterIdString)
        if (chapterIndex >= 0) {
          fileLogger.log('Found chapter at index:', chapterIndex)
          nextTrack = tracks[chapterIndex]
        } else {
          fileLogger.log('Chapter not found in tracks, starting from first chapter')
        }
      } else {
        fileLogger.log('No specific chapter, starting from first chapter')
      }

      await this.beginOwnedVideoStart(nextTrack)
      if (!this.isCurrentPlaybackRequest(requestId)) return
      this.setTrack(nextTrack)
      
      // Only update the position to 0 if we're not resuming from a saved position
      // Skip position update for video content as VideoPlayer handles it
      if (!skipPositionUpdate && tracks[0]?.type !== 'video') {
        fileLogger.log('Updating playback position to 0')
        this.updatePlaybackPosition(0)
      } else {
        fileLogger.log('Skipping position update to preserve saved position or video content')
      }

      if (startPosition !== undefined && startPosition > 0) {
        this.pendingSeekPosition = startPosition
        // Show resume time immediately — setTrack resets currentTime to 0
        this.currentTime = startPosition
      } else {
        this.pendingSeekPosition = null
      }
      
      // Play the track
      await this.play()
      
      // Return a resolved promise
      return Promise.resolve()
    },

    setPlaybackRate(rate: number) {
      if (this.sound) {
        this.sound.rate(rate)
      }
    },

    // New method to prepare the last played title without playing it
    async prepareLastPlayedTitle() {
      fileLogger.log('Preparing last played title without playing it')
      
      try {
        // Get library store
        const libraryStore = useLibraryStore()
        
        // Check if we have any played content
        if (libraryStore.played_content.length === 0) {
          fileLogger.log('No played content found')
          return false
        }
        
        // Get most recently played title (assuming the list is in reverse chronological order)
        const lastPlayed = libraryStore.played_content[0]
        fileLogger.log('Last played title:', lastPlayed)
        
        if (!lastPlayed) {
          return false
        }

        const authStore = useAuthStore()
        const resolvedTitle = await this.resolvePlaybackTitle(lastPlayed)
        this.currentTitle = resolvedTitle
        
        // CRITICAL: Handle both chapter_id (from mobile sync) and queueIndex (from web or mobile)
        // chapter_id is the actual chapter database ID, queueIndex can be either:
        // - chapter_id (when stored by web app)
        // - playlist index (when synced from mobile app)
        let targetChapter: Chapter | undefined = undefined
        
        if (resolvedTitle.chapters && resolvedTitle.chapters.length > 0) {
          // First, try to resolve by chapter_id if present (from mobile sync)
          const chapterId = (lastPlayed as any).chapter_id
          if (chapterId !== undefined && chapterId !== null) {
            const chapterIdString = String(chapterId)
            fileLogger.log('Found chapter_id from sync, searching for chapter:', chapterIdString)
            
            targetChapter = resolvedTitle.chapters.find(ch => 
              String(ch.id) === chapterIdString
            )
            
            if (targetChapter) {
              fileLogger.log('Found chapter by chapter_id:', targetChapter)
            } else {
              fileLogger.warn('Could not find chapter with chapter_id:', chapterIdString)
            }
          }
          
          // If chapter_id lookup failed, try queueIndex
          if (!targetChapter && lastPlayed.queueIndex !== undefined && lastPlayed.queueIndex !== null) {
            const queueIndexValue = lastPlayed.queueIndex
            fileLogger.log('Trying queueIndex:', queueIndexValue)
            
            // First, try to find chapter by ID (for web-native data where queueIndex contains chapter_id)
            const queueIndexString = String(queueIndexValue)
            targetChapter = resolvedTitle.chapters.find(ch => 
              String(ch.id) === queueIndexString
            )
            
            if (targetChapter) {
              fileLogger.log('Found chapter by queueIndex (treated as chapter_id):', targetChapter)
            } else {
              // If not found by ID, try using queueIndex as array index (for mobile-synced data)
              const queueIndexNum = typeof queueIndexValue === 'number' 
                ? queueIndexValue 
                : parseInt(String(queueIndexValue), 10)
              
              if (!isNaN(queueIndexNum) && queueIndexNum >= 0 && queueIndexNum < resolvedTitle.chapters.length) {
                targetChapter = resolvedTitle.chapters[queueIndexNum]
                fileLogger.log('Found chapter by queueIndex (treated as array index):', targetChapter)
              } else {
                fileLogger.warn('queueIndex is not a valid array index:', queueIndexNum)
              }
            }
          }

          const chaptersToPlay = resolveChaptersForPlayback(resolvedTitle, {
            purchased: libraryStore.purchased.some(
              t => String(t.id) === String(resolvedTitle.id)
            ),
            subscribed: Boolean(authStore.user?.subscribed),
          })

          if (chaptersToPlay?.length) {
            const tracks = chaptersToTracks(resolvedTitle, chaptersToPlay)
            this.setQueue(tracks)
            this.setPeekPlaybackSession(resolvedTitle, chaptersToPlay)

            if (targetChapter) {
              const targetTrack = tracks.find(
                track => String(track.id) === String(targetChapter!.id)
              )
              this.currentTrack = targetTrack ?? tracks[0]
              fileLogger.log('Prepared track from chapter:', targetChapter.name)
            } else {
              this.currentTrack = tracks[0]
            }
          } else if (targetChapter) {
            fileLogger.log('Creating track from specific chapter:', targetChapter.name)
            const tracks = chaptersToTracks(resolvedTitle, [targetChapter])
            if (tracks.length > 0) {
              this.setQueue(tracks)
              this.currentTrack = tracks[0]
              fileLogger.log('Created track from chapter:', targetChapter.name)
            } else {
              const track = contentToTrack(resolvedTitle)
              if (track.id) {
                track.id = String(track.id)
              }
              this.currentTrack = track
              this.setQueue([track])
            }
          } else {
            const track = contentToTrack(resolvedTitle)
            if (track.id) {
              track.id = String(track.id)
            }
            this.currentTrack = track
            this.setQueue([track])
          }
        } else {
          // No chapters, use contentToTrack
          const track = contentToTrack(resolvedTitle)
          if (track.id) {
            track.id = String(track.id)
          }
          this.currentTrack = track
          this.setQueue([track])
        }
        
        // Store queue position if available (web queuePosition or API position)
        const resumeAt = resolvePlayedAudioPosition(lastPlayed)
        if (resumeAt !== null && resumeAt > 0) {
          this.currentTime = resumeAt
          this.pendingSeekPosition = resumeAt
        }
        
        // Load audio but don't play
        if (this.currentTrack) {
          fileLogger.log('Preloading audio without playing')
          
          if (!this.sound || (this.sound as any)._src !== this.currentTrack.url) {
            if (this.sound) {
              this.sound.unload()
            }
            
            // Validate URL - make sure it's a non-empty string
            const audioUrl = this.currentTrack.url;
            if (!audioUrl || typeof audioUrl !== 'string') {
              fileLogger.error('Invalid URL for audio:', audioUrl);
              return false;
            }
            
            this.sound = new Howl({
              src: [audioUrl],
              html5: true,
              format: determineAudioFormats(audioUrl),
              volume: this.volume,
              preload: true,
              onend: () => {
                fileLogger.log('Track ended')
                this.isPlaying = false
                this.handleTrackEnd()
              },
              onload: () => {
                fileLogger.log('Audio preloaded successfully')
                this.duration = this.sound?.duration() ?? 0
              }
            })
          }
        }
        
        fileLogger.log('Last played title prepared successfully')
        return true
      } catch (error) {
        fileLogger.error('Error preparing last played title:', error)
        return false
      }
    },

    // Play a preview: dedicated peek/preview file in full, otherwise first 20s of episode 1
    async playPreview(content: Title) {
      fileLogger.log('Playing preview for:', content.title)

      if (!(await ensureTitlePlaybackAllowed(content))) return

      // EPUB previews open the reader — never start audio/video "playing" state
      if (getConfig().features.epubReader) {
        fileLogger.log('Ignoring playPreview — EPUB apps use reader sneak preview')
        return Promise.resolve()
      }

      const resolvedContent = await this.resolvePlaybackTitle(content)

      if (!resolvedContent.chapters?.length) {
        fileLogger.log('No chapters found, returning')
        return Promise.resolve()
      }
      
      try {
        // Soft prepare — do NOT call reset()/null videoPlayerMode here.
        // Full reset unmounts VideoPlayer and races remount + playWhenReady → stuck buffering.
        this._isStartingPreview = true
        this.isPreviewMode = true
        this.peekPlaybackSession = false
        this.pendingSeekPosition = null

        if (this.sound) {
          this.sound.stop()
          this.sound.unload()
          this.sound = null
        }
        this.stopProgressUpdates()

        if (this._previewTimeout) {
          clearTimeout(this._previewTimeout)
          this._previewTimeout = null
        }

        if (this.videoService) {
          try {
            await this.videoService.pause()
          } catch (error) {
            fileLogger.warn('Could not pause video before preview:', error)
          }
        }

        this.isPlaying = false
        this.currentTime = 0
        this.duration = 0
        
        fileLogger.log('Entering preview mode - will not store in library')
        
        // Prefer dedicated peek/preview files; otherwise fall back to first episode (time-capped)
        const peekChapters = getActivePeekPreviewChapters(resolvedContent.chapters)
        const hasExplicitPreviewFile = peekChapters.length > 0
        const chaptersForPreview = hasExplicitPreviewFile
          ? peekChapters
          : sortChaptersActive(resolvedContent.chapters)

        this._previewClipLimited = !hasExplicitPreviewFile
        fileLogger.log(
          hasExplicitPreviewFile
            ? 'Using explicit peek preview file(s) — playing in full'
            : `No explicit preview file — clipping first episode to ${PREVIEW_CLIP_SECONDS}s`
        )
        
        // Convert chapters to tracks
        const tracks = chaptersToTracks(resolvedContent, chaptersForPreview)
        const previewTrack = tracks[0]

        // Set the queue with only the first track
        this.setQueue([previewTrack])
        this.currentTitle = resolvedContent
        
        // Set the first track
        this.setTrack(previewTrack)

        if (previewTrack.type === 'video' && getConfig().features.video) {
          if (!this.videoPlayerMode) {
            this.setVideoPlayerMode('inline')
          }

          fileLogger.log('Starting video preview from beginning')
          this.isPlaying = true
          await this.playVideo({ forceFromStart: true })
          this.currentTime = 0
          if (this._previewClipLimited) {
            this.schedulePreviewTimeout(resolvedContent)
          }
          this._isStartingPreview = false
          return Promise.resolve()
        }

        // Validate URL - make sure it's a non-empty string
        const audioUrl = this.currentTrack!.url;
        if (!audioUrl || typeof audioUrl !== 'string') {
          fileLogger.error('Invalid URL for preview audio:', audioUrl);
          throw new Error('Invalid audio URL for preview');
        }
        
        // Create a new sound instance specifically for preview
        this.sound = new Howl({
          src: [audioUrl],
          html5: true,
          format: determineAudioFormats(audioUrl),
          volume: this.volume,
          onend: () => {
            fileLogger.log('Preview track ended naturally')
            this.stopPreview()
          },
          onplay: () => {
            fileLogger.log('Preview track started playing')
            this.isPlaying = true
            this.sound?.seek(0)
            this.currentTime = 0
            this.startProgressUpdates()
            if (this._previewClipLimited) {
              this.schedulePreviewTimeout(resolvedContent)
            }
          },
          onpause: () => {
            fileLogger.log('Preview track paused')
            this.isPlaying = false
          },
          onstop: () => {
            fileLogger.log('Preview track stopped')
            this.isPlaying = false
          },
          onload: () => {
            fileLogger.log('Preview track loaded, duration:', this.sound?.duration())
            this.duration = this.sound?.duration() ?? 0
          },
          onplayerror: () => {
            this.sound?.once('unlock', () => {
              this.sound?.play()
            })
          },
        })
        
        fileLogger.log('Starting to play the sound')
        
        if (this.sound) {
          this.sound.seek(0)
          this.currentTime = 0
          const playId = this.sound.play()
          fileLogger.log('Sound play initiated with ID:', playId)
          this.isPlaying = true
          
          setTimeout(() => {
            if (this.sound && this.sound.playing(playId)) {
              this._isStartingPreview = false
            } else if (this.sound) {
              this.sound.seek(0)
              this.sound.play()
              this.isPlaying = true
              setTimeout(() => {
                this._isStartingPreview = false
              }, 500)
            } else {
              this._isStartingPreview = false
            }
          }, 200)
        } else {
          this._isStartingPreview = false
        }
        
        return Promise.resolve()
      } catch (error) {
        fileLogger.error('Error in playPreview:', error)
        this.isPreviewMode = false
        this._isStartingPreview = false
        this._previewClipLimited = false
        this.isPlaying = false
        return Promise.resolve()
      }
    },

    // New method to initialize Media Session API
    initializeMediaSession() {
      if (!isMediaSessionSupported) return

      fileLogger.log('Initializing Media Session API')

      try {
        // Set action handlers for media session
        navigator.mediaSession.setActionHandler('play', () => {
          fileLogger.log('Play action triggered')
          this.play()
        })

        navigator.mediaSession.setActionHandler('pause', () => {
          fileLogger.log('Pause action triggered')
          this.pause()
        })

        navigator.mediaSession.setActionHandler('seekbackward', (details) => {
          if (this.isPreviewMode) return
          const skipTime = details?.seekOffset || 15
          fileLogger.log(`[MediaSession] Seek backward ${skipTime}s`)
          this.seek(Math.max(this.currentTime - skipTime, 0))
        })

        navigator.mediaSession.setActionHandler('seekforward', (details) => {
          if (this.isPreviewMode) return
          const skipTime = details?.seekOffset || 15
          fileLogger.log(`[MediaSession] Seek forward ${skipTime}s`)
          this.seek(Math.min(this.currentTime + skipTime, this.duration))
        })

        navigator.mediaSession.setActionHandler('previoustrack', () => {
          if (this.isPreviewMode) return
          fileLogger.log('Previous track')
          this.previous()
        })

        navigator.mediaSession.setActionHandler('nexttrack', () => {
          if (this.isPreviewMode) return
          fileLogger.log('Next track')
          this.next()
        })

        // Set position state update handler for progress
        if ('setPositionState' in navigator.mediaSession) {
          this.updatePositionState()
        }
      } catch (error) {
        fileLogger.error('Error initializing Media Session API:', error)
      }
    },

    // Update playback position information for scrubbing in media controls
    updatePositionState() {
      // First check if Media Session API is supported at all
      if (!isMediaSessionSupported || !('setPositionState' in navigator.mediaSession)) {
        fileLogger.log('Media Session API not supported, skipping position state update')
        return
      }

      // Check if we have a current track
      if (!this.currentTrack) {
        fileLogger.log('No current track, skipping position state update')
        return
      }

      // Check if sound object exists and is properly initialized
      if (!this.sound) {
        fileLogger.log('No sound object, skipping position state update')
        return
      }

      try {
        // Safely get the playback rate
        let playbackRate = 1
        
        // Only try to get the playback rate if the sound object is fully initialized
        // Check for the existence of the state method and if it returns 'loaded'
        const isSoundLoaded = this.sound && 
                            typeof this.sound.state === 'function' && 
                            this.sound.state() === 'loaded';
                            
        if (isSoundLoaded && typeof this.sound.rate === 'function') {
          try {
            playbackRate = this.sound.rate() || 1
          } catch (e) {
            fileLogger.error('Error getting playback rate:', e)
            // Continue with default rate of 1
          }
        }

        // Set the position state with safe values
        navigator.mediaSession.setPositionState({
          duration: this.duration || 0,
          playbackRate: playbackRate,
          position: this.currentTime || 0
        })
        
        fileLogger.log('Position state updated successfully')
      } catch (error) {
        // Catch any unexpected errors to avoid breaking playback
        fileLogger.error('Error updating position state:', error)
      }
    },

    // New method to update Media Session metadata
    updateMediaSession() {
      // Check if Media Session API is supported
      if (!isMediaSessionSupported) {
        fileLogger.log('Media Session API not supported, skipping metadata update')
        return
      }

      // Check if we have a current track
      if (!this.currentTrack) {
        fileLogger.log('No current track, skipping metadata update')
        return
      }

      try {
        fileLogger.log('Updating Media Session metadata')

        // Create artwork array with different sizes if available
        const artwork = []
        if (this.currentTrack.artwork) {
          artwork.push({ 
            src: this.currentTrack.artwork,
            sizes: '512x512', // Default size, adjust based on your actual artwork
            type: 'image/jpeg' // Adjust based on your actual image type
          })
        }

        // Set metadata
        navigator.mediaSession.metadata = new MediaMetadata({
          title: this.currentTrack.title || 'Unknown Title',
          artist: this.currentTrack.artist || 'Unknown Artist',
          artwork: artwork
        })

        // Update position state, but only if the sound object is properly initialized
        // Wrap this in a try/catch to handle any potential errors
        try {
          if (this.sound && typeof this.sound.rate === 'function') {
            this.updatePositionState()
          }
        } catch (error) {
          fileLogger.error('Error updating position state from media session update:', error)
        }
      } catch (error) {
        // Catch any unexpected errors to avoid breaking playback
        fileLogger.error('Error updating media session metadata:', error)
      }
    },

    // Reset player store state and clean up resources
    reset() {
      fileLogger.log('Resetting player state')
      
      // Stop any ongoing playback
      if (this.sound) {
        this.sound.stop()
        this.sound.unload()
        this.sound = null
      }

      // Pause parked video so a later preview/play does not inherit a stalled HLS load
      if (this.videoService) {
        try {
          this.videoService.pause()
        } catch (error) {
          fileLogger.warn('Could not pause video during reset:', error)
        }
      }
      
      // Clear any intervals
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
      
      // Clear any preview timeout
      if (this._previewTimeout) {
        fileLogger.log('Clearing preview timeout')
        clearTimeout(this._previewTimeout)
        this._previewTimeout = null
      }
      
      // Reset all state
      this.currentTrack = null
      this.queue = []
      this.originalQueue = []
      this.isPlaying = false
      this.currentTime = 0
      this.duration = 0
      // Don't reset volume as it's a user preference
      this.repeatMode = 'off'
      this.audioService = null
      // Keep videoService instance (parked player) — only clear playback intent above
      this.currentTitle = null
      this.lastSyncTime = 0
      this.lastSaveTime = 0
      
      // Also reset video player mode
      this.videoPlayerMode = null
      this.isPreviewMode = false
      this._isStartingPreview = false
      this._isStartingVideoPlayback = false
      this._videoStartGeneration++
      this._playbackGeneration++
      this._previewClipLimited = false
      this.peekPlaybackSession = false
      this.pendingSeekPosition = null
      
      // Clear media session if available
      if (navigator.mediaSession) {
        navigator.mediaSession.metadata = null
        navigator.mediaSession.playbackState = 'none'
      }
      
      fileLogger.log('Player state reset complete')
    },

    // Method to stop preview mode
    stopPreview() {
      fileLogger.log('Stopping preview mode')
      
      // Instead of just clearing preview-related state, do a full reset
      // This ensures complete cleanup and prevents any unauthorized playback
      this.reset();
      
      fileLogger.log('Preview mode stopped completely with full player reset')
    },

    // Video player methods
    async playVideo(options: { forceFromStart?: boolean } = {}) {
      // Claim ownership for this start. beginOwnedVideoStart may already have bumped
      // the generation; resume/play without it needs its own claim.
      if (!this._isStartingVideoPlayback) {
        this._videoStartGeneration++
      }
      const startGen = this._videoStartGeneration
      this._isStartingVideoPlayback = true

      try {
        if (!this.currentTrack || this.currentTrack.type !== 'video') {
          fileLogger.log('No video track to play')
          return
        }

        // Check if video feature is enabled
        const config = getConfig()
        if (!config.features.video) {
          fileLogger.warn('Video feature is not enabled, cannot play video')
          return
        }

        fileLogger.log('Playing video track:', this.currentTrack.title)
        fileLogger.log('Video track details:', {
          title: this.currentTrack.title,
          url: this.currentTrack.url,
          type: this.currentTrack.type,
          artist: this.currentTrack.artist
        })

        // For video content, ensure the full chapter queue exists for end-of-title handling
        this.ensurePlaybackQueueForCurrentTitle()

        // Initialize VideoService if not already done - dynamically import only when needed
        if (BUILD_FEATURES.video && !this.videoService) {
          const { VideoService: VideoServiceClass } = await import('@/services/video.service')
          if (startGen !== this._videoStartGeneration) return
          this.videoService = VideoServiceClass.getInstance()
          fileLogger.log('VideoService initialized in player store')
        }

        const videoUrl = this.getVideoUrlForCurrentTrack()
        if (!videoUrl) {
          fileLogger.error('No video URL found for track:', this.currentTrack)
          this.isPlaying = false
          return
        }

        const resumeAt = this.pendingSeekPosition
        const isNewSource = !this.videoService?.isAssignedSource(videoUrl)
        if (isNewSource && !resumeAt) {
          this.currentTime = 0
        }

        // Do not remount mini → inline until the new source is playing.
        // Moving the element during src() kills MediaSource / VHS.
        const onDetail = this.isOnCurrentTitleDetailPage()
        if (!this.videoPlayerMode) {
          this.setVideoPlayerMode('inline')
        }

        // Play intent before the host finishes mounting so VideoPlayer resumes on mount
        this.isPlaying = true

        fileLogger.log('Starting video via playWhenReady:', videoUrl, options)
        try {
          if (!this.videoService) {
            fileLogger.error('VideoService not available after init')
            this.isPlaying = false
            return
          }
          await this.videoService.playWhenReady(videoUrl, {
            forceFromStart: Boolean(
              options.forceFromStart || this.isPreviewMode || (isNewSource && !resumeAt)
            ),
          })
          // A newer title hop superseded this start — leave ownership to that generation
          if (startGen !== this._videoStartGeneration) return
          if (!this.videoService.isAssignedSource(videoUrl)) {
            fileLogger.log('Skipping stale playVideo completion — source changed')
            return
          }
          if (onDetail && this.videoPlayerMode !== 'inline' && this.videoService.isCurrentSourceReady()) {
            this.setVideoPlayerMode('inline')
          }
          if (this.pendingSeekPosition !== null && this.pendingSeekPosition > 0) {
            const seekTo = this.pendingSeekPosition
            this.pendingSeekPosition = null
            if (this.videoService.hasSource(videoUrl)) {
              this.videoService.seek(seekTo)
              this.currentTime = seekTo
            } else {
              this.pendingSeekPosition = seekTo
            }
          } else if (isNewSource) {
            this.currentTime = 0
          }
          fileLogger.log('Video playback started via VideoService')
        } catch (error) {
          fileLogger.error('Failed to start video playback:', error)
          if (startGen === this._videoStartGeneration && !this.videoService?.isAssignedSource(videoUrl)) {
            this.isPlaying = false
          }
          return
        }

        this.startProgressUpdates()
        
        // Update media session for video
        this.updateMediaSession()
      } catch (error) {
        fileLogger.error('Failed to play video:', error)
        if (startGen === this._videoStartGeneration) {
          this.isPlaying = false
        }
      } finally {
        if (startGen === this._videoStartGeneration) {
          this._isStartingVideoPlayback = false
        }
      }
    },

    setupVideoEventListeners() {
      if (!this.videoService) return

      this.videoService.on('play', () => {
        this.isPlaying = true
        this.updateMediaSession()
      })

      this.videoService.on('pause', () => {
        this.isPlaying = false
      })

      this.videoService.on('ended', () => {
        this.isPlaying = false
        this.handleTrackEnd()
      })

      this.videoService.on('timeupdate', (currentTime: number) => {
        this.currentTime = currentTime
        this.updatePositionState()
      })

      this.videoService.on('durationchange', (duration: number) => {
        this.duration = duration
      })

      this.videoService.on('error', (error: any) => {
        fileLogger.error('Video player error:', error)
        this.isPlaying = false
      })
    },

    pauseVideo() {
      if (this.currentTrack?.type === 'video') {
        // Actually pause the video using VideoService
        if (this.videoService) {
          try {
            this.videoService.pause()
            fileLogger.log('Video paused via VideoService')
          } catch (error) {
            fileLogger.error('Failed to pause video:', error)
          }
        }
        
        this.isPlaying = false
        this.stopProgressUpdates()
      }
    },

    seekVideo(time: number) {
      if (this.currentTrack?.type !== 'video') return

      if (this.isPreviewMode && time > 0) {
        fileLogger.log('Video seek blocked in preview mode')
        return
      }

      const url = this.getVideoUrlForCurrentTrack()
      if (url && this.videoService && !this.videoService.hasSource(url)) {
        fileLogger.log('Deferring video seek until source is ready:', time)
        this.pendingSeekPosition = time
        return
      }

      this.currentTime = time
      if (this.videoService) {
        try {
          this.videoService.seek(time)
        } catch (error) {
          fileLogger.error('Failed to seek video:', error)
        }
      }

      if (!this.isPreviewMode) {
        this.updatePlaybackPosition(time)
      }
    },

    setVideoVolume(volume: number) {
      if (this.currentTrack?.type === 'video') {
        this.volume = volume
        // The actual volume setting will be handled by the video element in the component
      }
    },

    getVideoCurrentTime(): number {
      if (this.videoService && this.currentTrack?.type === 'video') {
        return this.videoService.getCurrentTime()
      }
      return 0
    },

    getVideoDuration(): number {
      if (this.videoService && this.currentTrack?.type === 'video') {
        return this.videoService.getDuration()
      }
      return 0
    },

    isVideoPlaying(): boolean {
      if (this.videoService && this.currentTrack?.type === 'video') {
        return this.videoService.isPlaying()
      }
      return false
    },

    destroyVideoPlayer() {
      if (this.videoService) {
        this.videoService.destroy()
        this.videoService = null
      }
    },

    setVideoPlayerMode(mode: 'mini' | 'inline' | 'full' | 'fullscreen' | null) {
      this.videoPlayerMode = mode
      fileLogger.log('Video player mode set to:', mode)
    }
  }
})
